import connect from 'react-redux/es/connect/connect';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import loadable from '@loadable/component';

import HeaderContainer from '../containers/HeaderContainer';
import CartPageContainer from '../pages/cart/CartPageContainer';
import ConfirmationPageContainer from '../containers/ConfirmationPageContainer';
import GuestPageContainer from '../containers/GuestPageContainer';
import GolfReservationPage from './GolfReservationPage';
import PasswordChangePageContainer from '../containers/pages/PasswordChangePageContainer';
import PaymentPageContainer from '../containers/PaymentPageContainer';
import ReviewPageContainer from '../pages/review/ReviewPageContainer';
import CompareActivitiesPageContainer from '../containers/CompareActivityPageContainer';
import ClientListPage from './admin/ClientListPage';
import ClientEditPage from '../pages/admin/clientedit';
import ClientUsersPage from '../pages/admin/clientusers';
import ErrorPage from '../pages/error';
import ActivityCardPageContainer from '../pages/activitycards/ActivityCardPageContainer';
import AuditlogPage from '../pages/admin/auditlog';
import ConfirmOrDenyBookingPage from './ConfirmOrDenyBookingPage';
import BookingsListContainer from '../containers/BookingsListContainer';
import BookingItemEditPage from '../pages/bookingitemedit/BookingItemEditPage';
import Footer from './Footer';
import DocumentEditorPage from '../pages/admin/documentedit';
import UserGroupEditPage from '../pages/admin/usergroupedit';
import UserGroupListPage from '../pages/admin/usergroups';
import ChainListPage from '../pages/admin/chains/ChainListPage';
import ChainEditPage from '../pages/admin/chainedit';
import ChainPriceItemEditPage from '../pages/admin/chainpriceitemeditpage';
import ChainCompaniesPage from '../pages/admin/chaincompanies';
import DiscountPackageListPage from '../pages/admin/discountpackages';
import DiscountPackageEditPage from '../pages/admin/discountpackageedit';
import ClientPaymentMethodsPage from '../pages/admin/clientpaymentmethods';

import { ActivityEditPage, ActivityPriceItemEditPage } from '../pages';
import LocationsPage from '../pages/admin/locations';
import LocationEditPage from '../pages/admin/locationedit';
import ReaderEditPage from '../pages/admin/readeredit';
import VendorPriceItemEditPage from '../pages/admin/vendorpriceitemedit';
import ClientPriceItemEditPage from '../pages/admin/clientpriceitemeditpage';
import SystemFeeEditPage from '../pages/admin/systemfeeedit';
import PayoutsPage from '../pages/admin/payouts';
import BookingPaymentsPage from '../pages/admin/bookingpayments';
import PayoutReportPage from '../pages/admin/payoutreports';
import ActivityTimeslotsPageContainer from '../containers/ActivityTimeslotsPageContainer';
import ManifestGuestsPage from '../pages/admin/manifestguests';
import BreadcrumbTrail from '../components/admin/BreadcrumbTrail';
import IslandsListPage from '../pages/admin/islands/IslandsListPage';
import IslandsEditPage from '../pages/admin/islands/IslandsEditPage';
import CancelReasonsListPage from '../pages/admin/cancelreasons/CancelReasonsListPage';
import CancelReasonsEditPage from '../pages/admin/cancelreasons/CancelReasonsEditPage';
import ClientPaymentMethodEditPage from '../pages/admin/clientpaymentmethodedit';
import SourcegroupsListPage from '../pages/admin/sourcegroups/SourcegroupsListPage';
import SourcegroupsEditPage from '../pages/admin/sourcegroups/SourcegroupsEditPage';
import SourceListPage from '../pages/admin/sources/SourceListPage';
import SourceEditPage from '../pages/admin/sources/SourceEditPage';
import ExpiringDocumentsPage from '../pages/admin/expiringdocuments';
import CustomBookingsPage from '../pages/custombookings/CustomBookingsPage';
import NegativeBalancesPage from '../pages/negativebalances/NegativeBalancesPage';
import ClientUserContainer from '../containers/ClientUserContainer';
import BatchListPage from '../pages/admin/batches/BatchListPage';
import BatchEditPage from '../pages/admin/batchedit';
import ClientActivitiesPage from '../pages/admin/clientactivities';
import ActivityImportPage from '../pages/admin/activityimport';
import PriceItemListPage from '../pages/admin/priceitems/PriceItemsListPage.js';
import CustomReportPage from '../pages/admin/customreports/CustomReportPage.js';
import PaymentLinkPage from '../pages/PaymentLinkPage';
import ManifestReportPage from '../pages/ManifestReportPage';
import CrossLogin from '../pages/crosslogin/CrossLogin.js';
import HotelsListPage from '../pages/admin/hotels/HotelsListPage.js';
import HotelEditPage from '../pages/admin/hotels/HotelEditPage.js';
import EmaillogPage from '../pages/admin/emaillogs/EmaillogPage.js';
import JwtOauthLogin from '../pages/jwtoauthlogin/JwtOauthLogin.js';
import UnconfirmedBookingsPage from '../pages/unconfirmedBookings/UnconfirmedBookingsPage.js';

const LoadableTicketEditPage = loadable(
    () => import(/* webpackChunkName: "TicketEditPage" */ '../pages/admin/ticketedit'),
    {
        fallback: <div></div>
    }
);

const LoadableActivityList = loadable(
    () => import(/* webpackChunkName: "ActivityList" */ './admin/ActivityList'),
    {
        fallback: <div></div>
    }
);

const LoadableActivityBookings = loadable(
    () => import(/* webpackChunkName: "ActivityBookings" */ './admin/ActivityBookings'),
    {
        fallback: <div></div>
    }
);

const LoadableTagList = loadable(
    () => import(/* webpackChunkName: "TagList" */ './admin/TagList'),
    {
        fallback: <div></div>
    }
);

const LoadableTagEditPage = loadable(
    () => import(/* webpackChunkName: "TagEditPage" */ '../pages/admin/tagedit/TagEditPage'),
    {
        fallback: <div></div>
    }
);

const LoadableClientStatsPage = loadable(
    () => import(/* webpackChunkName: "ClientStatsPage" */ './admin/ClientStatsPage'),
    {
        fallback: <div></div>
    }
);

const App = ({ user }) => {
    const location = useLocation();
    const isPaymentLinkPage = location.pathname.startsWith('/pay/');
    // Same chrome-free treatment: the vendor arriving from the email has no
    // account, so a header full of signed-in navigation is noise.
    const isManifestReportPage = location.pathname.startsWith('/manifest-report/');
    const isBarePage = isPaymentLinkPage || isManifestReportPage;

    return (
        <>
            {!isBarePage && <HeaderContainer />}
            <div id="page-container" className={user && user.role === 'agent' ? 'is-agent' : ''}>
                {!isBarePage && <BreadcrumbTrail />}
                <Switch>
                    <Route path="/jwt-oauth-login">
                        <JwtOauthLogin />
                    </Route>
                    {/* Cross-site login */}
                    <Route path="/crosslogin">
                        <CrossLogin />
                    </Route>
                    {/* normal user features */}
                    <Route path="/" exact>
                        <ActivityCardPageContainer />
                    </Route>
                    <Route path="/activities/vendor/:vendorId">
                        <ActivityCardPageContainer />
                    </Route>
                    <Route path="/golf">
                        <GolfReservationPage />
                    </Route>
                    <Route exact path="/reset/:token">
                        <PasswordChangePageContainer />
                    </Route>
                    <Route path="/guests/:guestId">
                        <GuestPageContainer />
                    </Route>
                    <Route path="/cart">
                        <CartPageContainer />
                    </Route>
                    <Route path="/review/:cartitemid">
                        <ReviewPageContainer />
                    </Route>
                    <Route path="/payment">
                        <PaymentPageContainer />
                    </Route>
                    <Route path="/pay/:token">
                        <PaymentLinkPage />
                    </Route>
                    {/* Public manifest download — linked from the vendor
                        manifest email, no login. */}
                    <Route path="/manifest-report/:id/:start/:hash">
                        <ManifestReportPage />
                    </Route>
                    <Route path="/confirmation">
                        <ConfirmationPageContainer />
                    </Route>
                    <Route path="/confirmbooking/:id/:hash">
                        <ConfirmOrDenyBookingPage pageType="confirm" />
                    </Route>
                    <Route path="/denybooking/:id/:hash">
                        <ConfirmOrDenyBookingPage pageType="deny" />
                    </Route>
                    <Route path="/client/bookings/:id">
                        <BookingItemEditPage />
                    </Route>
                    <Route path="/client/bookings">
                        <BookingsListContainer />
                    </Route>
                    <Route path="/client/custombookings">
                        <CustomBookingsPage />
                    </Route>
                    <Route path="/client/expiredunconfirmedbookings">
                        <UnconfirmedBookingsPage />
                    </Route>
                    <Route path="/client/negative-balances">
                        <NegativeBalancesPage />
                    </Route>
                    <Route path="/compare/:activityId1/:activityId2/:activityId3?">
                        <CompareActivitiesPageContainer />
                    </Route>
                    <Route path="/emaillog">
                        <EmaillogPage />
                    </Route>

                    {/* admin features */}
                    <Route path="/admin/client/list">
                        <ClientListPage />
                    </Route>
                    <Route path="/admin/client/stats/:clientId">
                        <LoadableClientStatsPage />
                    </Route>
                    <Route path="/admin/client/:clientId/vendorpriceitems">
                        <VendorPriceItemEditPage />
                    </Route>
                    <Route path="/admin/client/:clientId/clientpriceitems">
                        <ClientPriceItemEditPage />
                    </Route>
                    <Route path="/admin/client/:clientId/clientactivities">
                        <ClientActivitiesPage />
                    </Route>
                    <Route path="/admin/client/:clientId/activity_import">
                        <ActivityImportPage />
                    </Route>
                    <Route path="/admin/client/:clientId/users/:userId">
                        <ClientUserContainer />
                    </Route>
                    <Route path="/admin/client/:clientId/users">
                        <ClientUsersPage />
                    </Route>
                    <Route path="/admin/client/:clientId/documents">
                        <DocumentEditorPage />
                    </Route>
                    <Route path="/admin/client/:clientId/usergroups/:groupId">
                        <UserGroupEditPage />
                    </Route>
                    <Route path="/admin/client/:clientId/usergroups">
                        <UserGroupListPage />
                    </Route>
                    <Route path="/admin/client/:clientId/discountpackages/:discountPackageId">
                        <DiscountPackageEditPage />
                    </Route>
                    <Route path="/admin/client/:clientId/discountpackages">
                        <DiscountPackageListPage />
                    </Route>
                    <Route path="/admin/client/:clientId/paymentmethods/:methodId">
                        <ClientPaymentMethodEditPage />
                    </Route>
                    <Route path="/admin/client/:clientId/paymentmethods">
                        <ClientPaymentMethodsPage />
                    </Route>
                    <Route path="/admin/client/:clientId">
                        <ClientEditPage />
                    </Route>
                    <Route path="/admin/activity/update/:activityId/tickets/:ticketId?">
                        <LoadableTicketEditPage />
                    </Route>
                    <Route path="/admin/activity/update/:activityId/priceitems">
                        <ActivityPriceItemEditPage />
                    </Route>
                    <Route path="/admin/activity/update/:activityId/">
                        <ActivityEditPage />
                    </Route>
                    <Route path="/admin/reports/payout">
                        <PayoutReportPage />
                    </Route>
                    <Route path="/admin/reports/custom">
                        <CustomReportPage />
                    </Route>
                    <Route path="/admin/systemfees/">
                        <SystemFeeEditPage />
                    </Route>
                    <Route path="/admin/terminallocations/:locationId">
                        <LocationEditPage />
                    </Route>
                    <Route path="/admin/terminallocations">
                        <LocationsPage />
                    </Route>
                    <Route path="/admin/bookingpayouts">
                        <PayoutsPage />
                    </Route>
                    <Route path="/admin/terminalreaders/:readerId">
                        <ReaderEditPage />
                    </Route>
                    <Route path="/admin/activities/list">
                        <LoadableActivityList />
                    </Route>
                    <Route path="/admin/activities/bookings/:id">
                        <LoadableActivityBookings />
                    </Route>
                    <Route path="/admin/activities/manifest" exact>
                        <Redirect to="/admin/activities/list" />
                    </Route>
                    <Route path="/admin/manifest/:id/:timeslot">
                        <ManifestGuestsPage />
                    </Route>
                    <Route path="/admin/manifest/:id">
                        <ActivityTimeslotsPageContainer />
                    </Route>
                    {/* Legacy routes */}
                    <Route path="/admin/activities/manifest/:id/guests">
                        <ManifestGuestsPage />
                    </Route>
                    <Route path="/admin/activities/manifest/:id">
                        <ActivityTimeslotsPageContainer />
                    </Route>
                    <Route path="/admin/tags/list">
                        <LoadableTagList />
                    </Route>
                    <Route path="/admin/tags/update/:tagId">
                        <LoadableTagEditPage />
                    </Route>
                    <Route path="/admin/auditlog">
                        <AuditlogPage />
                    </Route>
                    <Route path="/admin/salesreports">
                        <BookingPaymentsPage />
                    </Route>
                    <Route path="/admin/chains/list">
                        <ChainListPage />
                    </Route>
                    <Route path="/admin/chains/update/:chainId/companies">
                        <ChainCompaniesPage />
                    </Route>
                    <Route path="/admin/chains/update/:chainId/priceitems">
                        <ChainPriceItemEditPage />
                    </Route>
                    <Route path="/admin/chains/update/:chainId">
                        <ChainEditPage />
                    </Route>
                    <Route path="/admin/regions/update/:islandId">
                        <IslandsEditPage />
                    </Route>
                    <Route path="/admin/regions">
                        <IslandsListPage />
                    </Route>
                    <Route path="/admin/cancelreasons/update/:cancelReasonId">
                        <CancelReasonsEditPage />
                    </Route>
                    <Route path="/admin/cancelreasons">
                        <CancelReasonsListPage />
                    </Route>
                    <Route path="/admin/sourcegroups/create">
                        <SourcegroupsEditPage />
                    </Route>
                    <Route path="/admin/sourcegroups/update/:sourceGroupId">
                        <SourcegroupsEditPage />
                    </Route>
                    <Route path="/admin/sourcegroups">
                        <SourcegroupsListPage />
                    </Route>
                    <Route path="/admin/sources/create">
                        <SourceEditPage />
                    </Route>
                    <Route path="/admin/sources/update/:sourceId">
                        <SourceEditPage />
                    </Route>
                    <Route path="/admin/sources">
                        <SourceListPage />
                    </Route>
                    <Route path="/admin/expiringdocuments">
                        <ExpiringDocumentsPage />
                    </Route>
                    <Route path="/admin/batches/list">
                        <BatchListPage />
                    </Route>
                    <Route path="/admin/batches/update/:batchId">
                        <BatchEditPage />
                    </Route>
                    <Route path="/admin/priceitemnames">
                        <PriceItemListPage />
                    </Route>
                    <Route path="/admin/hotels/new">
                        <HotelEditPage />
                    </Route>
                    <Route path="/admin/hotels/update/:hotelId">
                        <HotelEditPage />
                    </Route>
                    <Route path="/admin/hotels">
                        <HotelsListPage />
                    </Route>
                    <Route>
                        <ErrorPage />
                    </Route>
                </Switch>
            </div>
            {!isBarePage && <Footer />}
        </>
    );
};

export default connect(state => ({ user: state.user.user }))(App);
